import {blogListSkeleton} from './_blogListSkeleton';

class BlogPagination {
   containerElement;
   currentPage = 0;
   _isLoading = false;

   constructor(containerElement) {
      this.containerElement = containerElement;
   }

   /**
    * Get the Blog list container
    * @returns {HTMLElement | null}
    */
   get blogListElement() {
      return this.containerElement.querySelector('.blog-post-list');
   }

   /**
    * Get the "load more" buttons
    * @returns {NodeListOf<HTMLElement> | null}
    */
   get loadMoreButtonElements() {
      return this.containerElement.querySelectorAll('.blog-load-more');
   }

   set isLoading(isLoading) {
      this._isLoading = isLoading;
      this.loadMoreButtonElements.forEach((buttonElement) => {
         buttonElement.disabled = isLoading;
      });
      if (isLoading) {
         this.addSkeletonListItems();
      } else {
         this.removeSkeletonListItems();
      }
   }

   get isLoading() {
      return this._isLoading;
   }

   /**
    * Fetch contents of n-th page
    * @param pageNum Page number
    * @returns {Promise<{hasNextPage: boolean, articles: (NodeListOf<HTMLElement>|*[])}>}
    */
   async fetchPage(pageNum) {
      // Build Url
      const templateUrl = this.blogListElement.dataset.url;
      const url = templateUrl.replaceAll(['{pageNum}'], [pageNum]);
      // Fetch content
      const response = await fetch(url);
      // Parse list content
      const text = await response.text();
      const dom = new DOMParser().parseFromString(text, 'text/html');
      // Gather elements
      const listPage = dom.querySelector('#blog-list-page');
      const articles = dom.querySelectorAll('article');
      return {
         hasNextPage: listPage?.dataset?.hasNext === 'true',
         articles: articles ?? [],
      };
   }

   /**
    * Load and append next page
    * @returns {Promise<void>}
    */
   loadNextPage() {
      if (this.isLoading) {
         return Promise.resolve();
      }
      this.isLoading = true;
      const pageToLoad = this.currentPage + 1;
      return this.fetchPage(pageToLoad).then((data) => {
         // Display articles
         for (const article of data.articles) {
            this.blogListElement.appendChild(article);
         }
         // Increase page number
         this.currentPage = pageToLoad;
         // If no next page: Hide button
         if (!data.hasNextPage) {
            this.loadMoreButtonElements.forEach((buttonElement) => {
               buttonElement.classList.add('no-next-page');
            });
         }
      }).finally(() => {
         this.isLoading = false;
      });
   }

   /**
    * Initialize Blog List Pagination
    */
   initialize() {
      this.loadMoreButtonElements.forEach((loadMoreBtn) => {
         loadMoreBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.loadNextPage();
         });
      });
      // TODO: DISPLAY SOME KIND OF LOADING INDICATOR(s)
      this.loadNextPage();
   }

   /**
    * Create Skeleton placeholder items and append them to the list
    * @param count {number} of items to add
    */
   addSkeletonListItems(count = 4) {
      const blogListElement = this.blogListElement;
      if (!blogListElement) {
         return;
      }
      for (let i = 0; i < count; i++) {
         blogListElement.insertAdjacentHTML('beforeend', blogListSkeleton);
      }
   }

   /**
    * Remove Skeleton placeholder items from the blog list
    */
   removeSkeletonListItems() {
      this.blogListElement.querySelectorAll('.blog-post-skeleton').forEach((skeletonElement) => {
         skeletonElement.remove();
      });
   }

}

// Initialize Blog Pagination
const blogHome = document.querySelector('.blog-home');
if (blogHome) {
   const blogPagination = new BlogPagination(blogHome);
   blogPagination.initialize();
}
